(function () {
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Luxury accordion timing.
  // Cards never animate individually. They sit still inside the panel and are
  // progressively unveiled by the growing mask, with a slight parallax lag on the
  // panel so the content settles rather than slides in.
  //
  // Duration scales with how far the mask actually travels. A fixed duration
  // makes desktop feel abrupt and mobile feel slow, because the same panel is
  // roughly three times taller on a phone (2 columns) than on a wide screen
  // (4 columns). Scaling by distance keeps the perceived speed identical.
  var PAUSE_MS = 140;

  function openDuration(px) {
    return Math.max(620, Math.min(1000, 430 + px * 0.32));
  }

  function closeDuration(px) {
    return Math.max(440, Math.min(720, 320 + px * 0.22));
  }

  function init(root) {
    if (!root || root.dataset.consInit === 'true') return;
    root.dataset.consInit = 'true';

    var ONE = root.getAttribute('data-one') || '';
    var MANY = root.getAttribute('data-many') || '';

    var pills = Array.prototype.slice.call(root.querySelectorAll('.cons__pill'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('.cons__card'));
    var reveal = root.querySelector('[data-cons-reveal]');
    var results = root.querySelector('[data-cons-results]');
    var countEl = root.querySelector('[data-cons-count]');
    var viewAll = root.querySelector('[data-cons-viewall]');

    if (!pills.length || !reveal || !results) return;

    var current = null;   // pill whose set is currently shown
    var busy = false;     // a sequence is mid-flight
    var queued = null;    // last pill clicked while busy
    var token = 0;

    function wait(ms) {
      return new Promise(function (res) { window.setTimeout(res, REDUCED ? 0 : ms); });
    }

    // Drives the mask and resolves only when the transition truly ends, so close
    // and open can never overlap. Duration comes from --open-ms alone: one source
    // of truth, shared with the panel's parallax.
    function maskTo(px, ms) {
      return new Promise(function (res) {
        reveal.style.setProperty('--open-ms', ms + 'ms');

        if (REDUCED) {
          reveal.style.height = px + 'px';
          res();
          return;
        }

        var from = reveal.getBoundingClientRect().height;
        reveal.style.height = from + 'px';
        void reveal.offsetHeight;
        reveal.style.height = px + 'px';

        var done = false;
        function finish(e) {
          if (e && e.propertyName !== 'height') return;
          if (done) return;
          done = true;
          reveal.removeEventListener('transitionend', finish);
          res();
        }
        reveal.addEventListener('transitionend', finish);
        window.setTimeout(finish, ms + 160);
      });
    }

    function paint(family) {
      var shown = 0;
      cards.forEach(function (card) {
        var list = (card.getAttribute('data-families') || '').split('|');
        var match = family !== '' && list.indexOf(family) !== -1;
        card.hidden = !match;
        if (match) shown++;
      });
      return shown;
    }

    function meta(pill, shown) {
      if (countEl) countEl.textContent = shown + ' ' + (shown === 1 ? ONE : MANY);
      if (viewAll) viewAll.setAttribute('href', pill.getAttribute('data-url'));
    }

    function setPillState(pill) {
      pills.forEach(function (p) {
        var on = p === pill;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function openMask(pill, mine) {
      var shown = paint(pill.getAttribute('data-family'));

      // Nothing to show: stay closed rather than opening an empty panel.
      if (shown === 0) {
        current = null;
        setPillState(null);
        return Promise.resolve();
      }

      meta(pill, shown);

      root.classList.add('is-open');
      reveal.setAttribute('aria-hidden', 'false');
      current = pill;

      results.classList.remove('is-in');
      void results.offsetHeight;

      var target = results.offsetHeight;
      var ms = openDuration(target);

      requestAnimationFrame(function () {
        results.classList.add('is-in');
      });

      return maskTo(target, ms).then(function () {
        if (mine !== token) return;
        reveal.style.height = 'auto';
      });
    }

    function closeMask(mine) {
      var from = reveal.getBoundingClientRect().height;

      root.classList.remove('is-open');
      results.classList.remove('is-in');

      return maskTo(0, closeDuration(from)).then(function () {
        if (mine !== token) return;
        reveal.setAttribute('aria-hidden', 'true');
        cards.forEach(function (card) { card.hidden = true; });
      });
    }

    // Runs a sequence under the busy lock, then applies whatever the visitor
    // clicked while it was running, so no input is silently dropped.
    function run(seq) {
      busy = true;
      return seq().then(function () {
        busy = false;
        var next = queued;
        queued = null;
        if (next) dispatch(next);
      });
    }

    function open(pill) {
      var mine = ++token;
      setPillState(pill);
      return run(function () { return openMask(pill, mine); });
    }

    function close() {
      var mine = ++token;
      current = null;
      setPillState(null);
      return run(function () { return closeMask(mine); });
    }

    // Close fully, hold a beat, load the new set, then open again.
    function switchTo(pill) {
      var mine = ++token;
      setPillState(pill);
      current = null;

      return run(function () {
        return closeMask(mine)
          .then(function () {
            if (mine !== token) return null;
            return wait(PAUSE_MS);
          })
          .then(function () {
            if (mine !== token) return;
            return openMask(pill, mine);
          });
      });
    }

    function dispatch(pill) {
      if (current === pill) {
        close();
      } else if (current) {
        switchTo(pill);
      } else {
        open(pill);
      }
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        // Mid-animation clicks are remembered, not applied on top of a running
        // sequence: that is what allowed one set to swap for another instantly.
        if (busy) {
          queued = pill;
          return;
        }
        dispatch(pill);
      });
    });

    // Re-measure only on a real width change. Mobile browsers fire resize when
    // the URL bar hides during scroll, which would otherwise snap the mask open.
    var lastWidth = window.innerWidth;
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      if (!current || busy) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (current && !busy) reveal.style.height = 'auto';
      }, 160);
    });

    if (root.getAttribute('data-preselect') === 'true' && pills[0]) open(pills[0]);
  }

  function initAll(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(ctx.querySelectorAll('.cons')).forEach(init);
    if (ctx !== document && ctx.matches && ctx.matches('.cons')) init(ctx);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  if (!window.consulenzaBound) {
    window.consulenzaBound = true;
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  }
})();
